<div class="p-6 space-y-8 animate-in fade-in duration-700">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
            <h1 class="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Administración Global</h1>
            <p class="text-slate-500 dark:text-slate-400 mt-1">Visión consolidada de toda la red de Dentium.</p>
        </div>
        <div class="flex items-center gap-2">
            <app-button variant="outline" icon="solar:refresh-bold" (click)="handleReload()" [loading]="statsResource.isLoading()">
                Sincronizar Datos
            </app-button>
            <app-button variant="primary" icon="solar:add-circle-bold" routerLink="/app/businesses">
                Nuevo Negocio
            </app-button>
        </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        @for (stat of statsResource.value()?.stats; track stat.label) {
        <app-card class="group hover:shadow-lg transition-all duration-300">
            <div class="flex items-start justify-between">
                <div class="space-y-2">
                    <p class="text-[10px] font-black uppercase text-slate-500/60 tracking-wider leading-none">{{ stat.label }}</p>
                    <h3 class="text-2xl font-black tracking-tighter text-slate-900 dark:text-white leading-none">{{ stat.value }}</h3>
                </div>
                <div [class]="'p-2.5 rounded-xl ' + stat.bg">
                    <iconify-icon [icon]="stat.icon" [class]="'text-xl ' + stat.color"></iconify-icon>
                </div>
            </div>
            <div class="mt-4 flex items-center gap-2">
                <span class="text-[10px] font-black px-2 py-0.5 rounded-full bg-foreground/[0.03] text-foreground/50 uppercase tracking-tighter">
                    {{ stat.trend }}
                </span>
            </div>
        </app-card>
        }
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Growth Chart -->
        <app-card class="lg:col-span-2">
            <app-section-header title="Crecimiento de la Red" description="Nuevos negocios registrados por mes" icon="solar:chart-line-bold-duotone"></app-section-header>
            
            <div class="h-[280px] w-full flex items-end gap-3 px-2 pt-8">
                @for (item of statsResource.value()?.chartData; track item.label) {
                <div class="flex-1 flex flex-col items-center gap-2 group cursor-pointer">
                    <div class="relative w-full flex flex-col justify-end h-full">
                        <div class="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            {{ item.value }} Negocios
                        </div>
                        <div class="w-full bg-primary-500/10 group-hover:bg-primary-500/30 transition-all rounded-t-lg relative"
                             [style.height.%]="(item.value / (maxChartValue || 1)) * 100">
                             <div class="w-full h-1 bg-primary-500 rounded-t-lg"></div>
                        </div>
                    </div>
                    <span class="text-[9px] font-black text-slate-500/60 uppercase">{{ item.label }}</span>
                </div>
                }
            </div>
        </app-card>

        <!-- Global Activity / Insights -->
        <app-card>
            <app-section-header title="Gestión de Plataforma" icon="solar:shield-star-bold-duotone"></app-section-header>
            <div class="space-y-6 mt-4">
                <div class="flex items-center gap-4">
                    <div class="p-3 rounded-xl bg-primary-500/10">
                        <iconify-icon icon="solar:hand-money-bold-duotone" class="text-2xl text-primary-500"></iconify-icon>
                    </div>
                    <div>
                        <p class="text-sm font-black text-slate-800 dark:text-white">Retención Mensual</p>
                        <p class="text-xs text-slate-500 font-bold">94% de renovaciones</p>
                    </div>
                </div>
                <div class="flex items-center gap-4">
                    <div class="p-3 rounded-xl bg-purple-500/10">
                        <iconify-icon icon="solar:crown-minimalistic-bold-duotone" class="text-2xl text-purple-500"></iconify-icon>
                    </div>
                    <div>
                        <p class="text-sm font-black text-slate-800 dark:text-white">Planes Premium</p>
                        <p class="text-xs text-slate-500 font-bold">72% de la base instalada</p>
                    </div>
                </div>
                <div class="flex items-center gap-4">
                    <div class="p-3 rounded-xl bg-emerald-500/10">
                        <iconify-icon icon="solar:graph-up-bold-duotone" class="text-2xl text-emerald-500"></iconify-icon>
                    </div>
                    <div>
                        <p class="text-sm font-black text-slate-800 dark:text-white">Escalabilidad</p>
                        <p class="text-xs text-slate-500 font-bold">Infraestructura al 15%</p>
                    </div>
                </div>
                <div class="pt-4 border-t border-slate-100 dark:border-slate-800">
                    <p class="text-[10px] text-slate-400 leading-tight font-bold italic">
                        Métricas de salud del sistema y facturación consolidada de suscripciones.
                    </p>
                </div>
            </div>
        </app-card>
    </div>

    <!-- Recent Businesses Table -->
    <app-card>
        <app-section-header title="Negocios Recientes" icon="solar:buildings-outline">
            <app-button variant="ghost" size="sm" routerLink="/app/businesses">Ver Todos</app-button>
        </app-section-header>

        <div class="mt-4 overflow-x-auto">
            <table class="w-full text-left border-collapse">
                <thead>
                    <tr class="text-slate-400 text-xs font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                        <th class="py-4 px-4">Negocio</th>
                        <th class="py-4 px-4">Correo</th>
                        <th class="py-4 px-4">Fecha Registro</th>
                        <th class="py-4 px-4">Estado</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-50 dark:divide-slate-900">
                    @for (biz of statsResource.value()?.recentBusinesses; track biz.email) {
                    <tr class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                        <td class="py-4 px-4">
                            <div class="flex items-center gap-3">
                                <div class="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-600 font-bold text-xs uppercase">
                                    {{ biz.name.substring(0, 2) }}
                                </div>
                                <span class="font-medium text-slate-900 dark:text-white">{{ biz.name }}</span>
                            </div>
                        </td>
                        <td class="py-4 px-4 text-slate-600 dark:text-slate-400 text-sm">{{ biz.email }}</td>
                        <td class="py-4 px-4 text-slate-600 dark:text-slate-400 text-sm">{{ biz.date }}</td>
                        <td class="py-4 px-4">
                            <span [class]="'px-2 py-1 rounded-full text-[10px] font-bold ' + (biz.status === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600')">
                                {{ biz.status }}
                            </span>
                        </td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
    </app-card>
</div>
